/**
 * Countdown — live "1d 23h left" style ticker for a target date/time.
 * Ticks once a second (matches the 1Hz-interval technique already used
 * elsewhere, e.g. SubstituteCallingSystem.js's auto-accept timer) but no
 * shared component existed yet, so
 * this is the first reusable one.
 *
 * Usage: <Countdown targetDate={entry.date} /> or any ISO datetime string.
 */
import React, { useEffect, useState } from 'react';

function formatRemaining(ms) {
  if (ms <= 0) return 'Due now';
  const totalMinutes = Math.floor(ms / (60 * 1000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

export default function Countdown({ targetDate, className = '' }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const target = new Date(targetDate);
  if (Number.isNaN(target.getTime())) return null;
  const remainingMs = target.getTime() - now.getTime();

  return (
    <span className={`font-mono tabular-nums ${className}`} data-testid="countdown">
      {formatRemaining(remainingMs)}
    </span>
  );
}
